'use client';

import {
  CSSProperties,
  FC,
  isValidElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { ArrowRightIcon } from '@/assets';
import DynamicMarquee from '@/components/DynamicMarquee';
import { usePrefersReducedMotion, useCountUp } from '@/hooks';
import HeroVelxEngineRedesign from './variants/Redesign';

function flattenRichTextToString(node: unknown): string {
  if (node == null) {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(flattenRichTextToString).join('');
  }
  if (isValidElement(node)) {
    return flattenRichTextToString(
      (node as { props?: { children?: unknown } }).props?.children,
    );
  }
  return '';
}

type StatCounterProps = {
  target: number;
  suffix: string;
  className?: string;
};

function StatCounter({ target, suffix, className }: StatCounterProps) {
  // M-05: render the real value in markup; count up only as enhancement.
  const { display, ref } = useCountUp<HTMLDivElement>(target);

  return (
    <div ref={ref}>
      <p className={className}>
        {display}
        {suffix}
      </p>
    </div>
  );
}

/**
 * Props for `HeroVelxEngine`.
 */
export type HeroVelxEngineProps =
  SliceComponentProps<Content.HeroVelxEngineSlice>;

const HeroVelxEngine: FC<HeroVelxEngineProps> = ({ slice, ...rest }) => {
  if (slice.variation === 'redesign') {
    return <HeroVelxEngineRedesign slice={slice} {...rest} />;
  }
  return <HeroVelxEngineCurrent slice={slice} {...rest} />;
};

/**
 * Component for "HeroVelxEngine" Slices.
 */
const HeroVelxEngineCurrent: FC<HeroVelxEngineProps> = ({ slice }) => {
  const { kicker, title, description, cta_link } = slice.primary;
  const stats = 'stats' in slice.primary ? (slice.primary.stats ?? []) : [];
  const textStatsField = (
    slice.primary as unknown as {
      text_stats?: { prefix?: string; accent?: string }[];
    }
  ).text_stats;
  const textStats = Array.isArray(textStatsField) ? textStatsField : [];
  const systemIntelligenceSubtitle = (
    slice.primary as unknown as { subtitle?: string }
  ).subtitle;
  const systemIntelligenceTagline = (
    slice.primary as unknown as { tagline?: string }
  ).tagline;

  const variation = slice.variation as string;
  const isVelxArchitect = variation === 'velxArchitect';
  const isPillars = variation === 'pillars';
  const isSystemIntelligence = variation === 'systemIntelligence';
  const isCenteredSimple = variation === 'centeredSimple';
  const isIndustriesGrid = variation === 'industriesGrid';
  const background_video =
    'background_video' in slice.primary ? slice.primary.background_video : null;
  const background_video_poster = (
    slice.primary as unknown as {
      background_video_poster?: import('@prismicio/client').ImageField;
    }
  ).background_video_poster;
  const secondary_cta_link = (
    'secondary_cta_link' in slice.primary
      ? slice.primary.secondary_cta_link
      : null
  ) as import('@prismicio/client').LinkField | null;
  const recognitionLogo = (
    slice.primary as unknown as {
      recognition_logo?: import('@prismicio/client').ImageField;
    }
  ).recognition_logo;
  const recognitionText = (
    slice.primary as unknown as { recognition_text?: string }
  ).recognition_text;
  const recognitionLink = (
    slice.primary as unknown as {
      recognition_link?: import('@prismicio/client').LinkField;
    }
  ).recognition_link;
  const hasRecognition =
    isFilled.image(recognitionLogo) ||
    !!recognitionText ||
    isFilled.link(recognitionLink);
  const heroHighlight = (slice.primary as unknown as { highlight?: string })
    .highlight;
  const logos = useMemo(() => {
    if ('logos' in slice.primary) {
      return slice.primary.logos ?? [];
    }
    return [];
  }, [slice.primary]);
  const showLogoLines = Boolean(
    (slice.primary as unknown as { show_logo_lines?: boolean }).show_logo_lines,
  );
  const scroll_down_image =
    'scroll_down_image' in slice.primary
      ? slice.primary.scroll_down_image
      : null;

  const showScrollHint = isFilled.image(scroll_down_image);

  // Defer mounting the background <video> until the browser is idle so the
  // hero text + poster paint first (M-19: hero video delays perceived load).
  // Reduced-motion users get the static poster instead of the looping video.
  const prefersReducedMotion = usePrefersReducedMotion();
  const [shouldMountVideo, setShouldMountVideo] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const schedule =
      'requestIdleCallback' in window
        ? window.requestIdleCallback
        : (cb: IdleRequestCallback) =>
            setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 0 }));
    const handle = schedule(() => setShouldMountVideo(true));
    return () => {
      if ('cancelIdleCallback' in window && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      }
    };
  }, []);
  const hasLogos = logos.length > 0;
  const scrollOverLogos = showScrollHint && hasLogos;
  const [isPaused, setIsPaused] = useState(false);

  const doubledLogos = useMemo(
    () => (logos.length > 0 ? [...logos, ...logos] : []),
    [logos],
  );

  if (isPillars) {
    const pillarsPrimary = slice.primary as unknown as {
      kicker?: string;
      cta_label?: string;
      cta_link?: import('@prismicio/client').LinkField;
      pillars?: Array<{
        icon?: import('@prismicio/client').ImageField;
        title?: string;
        description?: string;
      }>;
      background_video?: import('@prismicio/client').LinkToMediaField;
      title?: typeof title;
      description?: typeof description;
    };
    const pillarsKicker = pillarsPrimary.kicker;
    const pillarsCtaLabel = pillarsPrimary.cta_label;
    const pillarsCtaLink = pillarsPrimary.cta_link;
    const pillarsList = pillarsPrimary.pillars ?? [];
    const pillarsBgVideo = pillarsPrimary.background_video;
    const hasPillars = pillarsList.length > 0;
    const pillarsExtras = slice.primary as unknown as {
      bottom_banner_logo?: import('@prismicio/client').ImageField;
      bottom_banner_text?: string;
      bottom_banner_cta?: import('@prismicio/client').LinkField;
    };
    const bottomBannerLogo = pillarsExtras.bottom_banner_logo;
    const bottomBannerText = pillarsExtras.bottom_banner_text;
    const bottomBannerCta = pillarsExtras.bottom_banner_cta;
    const hasBottomBanner =
      isFilled.image(bottomBannerLogo) ||
      !!bottomBannerText ||
      isFilled.link(bottomBannerCta);

    return (
      <div
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="font-inter"
      >
        <section className="relative mx-[30px] overflow-hidden rounded-[24px] bg-[#01060a]">
          {isFilled.linkToMedia(pillarsBgVideo) && (
            <div className="absolute inset-0 overflow-hidden">
              {isFilled.image(background_video_poster) && (
                <PrismicNextImage
                  field={background_video_poster}
                  priority
                  fill
                  sizes="100vw"
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                  fallbackAlt=""
                />
              )}
              {shouldMountVideo && !prefersReducedMotion && (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="none"
                  poster={background_video_poster?.url ?? undefined}
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                >
                  <source src={pillarsBgVideo.url} type="video/webm" />
                  <source src={pillarsBgVideo.url} type="video/mp4" />
                </video>
              )}
              <div className="absolute inset-0 bg-[#01060a]/60" />
            </div>
          )}

          <div className="relative mx-auto w-full max-w-[1200px] px-4 pt-8 pb-16 md:px-8 md:pt-10 md:pb-20">
            <div
              className="relative overflow-hidden rounded-[40px] px-6 py-12 text-center md:px-16 md:py-20"
              style={{
                background: 'rgba(0, 0, 0, 0.40)',
                backdropFilter: 'blur(10.1px)',
                WebkitBackdropFilter: 'blur(10.1px)',
              }}
            >
              {pillarsKicker && (
                <div className="mb-6 flex justify-center">
                  <span className="rounded-full border border-[#00F557] px-4 py-2 text-[12px] font-normal uppercase tracking-[0.04em] leading-[1.2] text-white md:text-[14px]">
                    {pillarsKicker}
                  </span>
                </div>
              )}

              {isFilled.richText(title) && (
                <div className="mx-auto max-w-[900px] [&_h1]:m-0 [&_h1]:text-[36px] md:[&_h1]:text-[56px] [&_h1]:font-semibold [&_h1]:leading-[1.15] [&_h1]:text-white">
                  <PrismicRichText field={title} />
                </div>
              )}

              {isFilled.richText(description) && (
                <div className="mx-auto mt-6 max-w-[820px] text-[16px] font-normal leading-[1.5] text-white/85 md:text-[18px] [&_p]:m-0 [&_p+p]:mt-2">
                  <PrismicRichText
                    field={description}
                    components={{
                      strong: ({ children }) => (
                        <strong className="font-semibold text-[#00F557]">
                          {children}
                        </strong>
                      ),
                      hyperlink: ({ children, node }) => (
                        <a
                          href={node.data.url ?? '#'}
                          className="text-[#00F557] underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {children}
                        </a>
                      ),
                    }}
                  />
                </div>
              )}

              {(pillarsCtaLabel || isFilled.link(pillarsCtaLink)) && (
                <div className="mt-8 flex justify-center">
                  {isFilled.link(pillarsCtaLink) ? (
                    <PrismicNextLink
                      field={pillarsCtaLink}
                      className="inline-flex items-center justify-center rounded-[100px] bg-[#00F557] px-6 py-3 font-manrope text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90"
                      style={{ borderTop: '1px solid #EAEAEA' }}
                    >
                      {pillarsCtaLabel || 'Learn more'}
                    </PrismicNextLink>
                  ) : (
                    <span
                      className="inline-flex items-center justify-center rounded-[100px] bg-[#00F557] px-6 py-3 font-manrope text-[16px] font-bold leading-[1.5] text-[#002726]"
                      style={{ borderTop: '1px solid #EAEAEA' }}
                    >
                      {pillarsCtaLabel}
                    </span>
                  )}
                </div>
              )}
            </div>

            {hasPillars && (
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
                {pillarsList.slice(0, 3).map((pillar, i) => (
                  <div
                    key={i}
                    className="flex flex-col items-center rounded-[24px] px-6 py-10 text-center"
                    style={{
                      background:
                        'linear-gradient(180deg, #041118 46.68%, rgba(0, 0, 0, 0.00) 100%), linear-gradient(270deg, #041118 0%, #13094A 50%, #041118 100%)',
                      backdropFilter: 'blur(21px)',
                      WebkitBackdropFilter: 'blur(21px)',
                    }}
                  >
                    {isFilled.image(pillar.icon) && (
                      <PrismicNextImage
                        field={pillar.icon}
                        className="mb-6 h-12 w-auto object-contain"
                        fallbackAlt=""
                      />
                    )}
                    {pillar.title && (
                      <h3 className="m-0 text-[22px] font-semibold leading-[1.2] text-white md:text-[24px]">
                        {pillar.title}
                      </h3>
                    )}
                    {pillar.description && (
                      <p className="mx-auto mt-3 max-w-[280px] text-[14px] font-normal leading-[1.5] text-white/75 md:text-[15px]">
                        {pillar.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {hasBottomBanner && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                {isFilled.image(bottomBannerLogo) && (
                  <PrismicNextImage
                    field={bottomBannerLogo}
                    className="h-[22px] w-auto object-contain"
                    fallbackAlt=""
                  />
                )}
                {bottomBannerText && (
                  <p
                    className="text-center text-[18px] font-medium leading-[1.4] text-white"
                    style={{ textShadow: '0px 0px 6px black' }}
                  >
                    {bottomBannerText}
                  </p>
                )}
                {isFilled.link(bottomBannerCta) && (
                  <PrismicNextLink
                    field={bottomBannerCta}
                    className="text-[16px] font-extrabold leading-[1.5] text-[#00f557] underline"
                    style={{ textShadow: '0px 0px 6px black' }}
                  />
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <section
        className={`relative mx-5 overflow-hidden rounded-[24px] bg-[#01060a] px-4 pt-[168px] pb-12 md:mx-[30px] md:px-8 lg:px-[150px] lg:pt-[156px] lg:pb-[64px] landscape-short:pt-24 landscape-short:pb-6 ${
          !isVelxArchitect && showScrollHint && !hasLogos
            ? 'pb-20 md:pb-24 lg:pb-[96px]'
            : ''
        } ${isVelxArchitect ? 'pb-8 lg:pb-[46px]' : ''}`}
      >
        {isFilled.linkToMedia(background_video) && (
          <div
            className={`absolute inset-0 overflow-hidden ${
              isVelxArchitect ? 'opacity-30' : 'opacity-20'
            }`}
          >
            {isFilled.image(background_video_poster) && (
              <PrismicNextImage
                field={background_video_poster}
                priority
                fill
                sizes="100vw"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
                fallbackAlt=""
              />
            )}
            {shouldMountVideo && !prefersReducedMotion && (
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="none"
                poster={background_video_poster?.url ?? undefined}
                className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
              >
                <source src={background_video.url} type="video/webm" />
                <source src={background_video.url} type="video/mp4" />
              </video>
            )}
          </div>
        )}

        {/* <div className="absolute inset-0 bg-gradient-to-b from-[#000814cc] via-[#01060af0] to-[#01060a]" /> */}

        {isIndustriesGrid ? (
          (() => {
            const industries = ((
              slice.primary as unknown as {
                industries?: {
                  icon?: import('@prismicio/client').ImageField;
                  title?: string;
                  link?: import('@prismicio/client').LinkField;
                }[];
              }
            ).industries ?? []) as {
              icon?: import('@prismicio/client').ImageField;
              title?: string;
              link?: import('@prismicio/client').LinkField;
            }[];
            return (
              <div className="relative mx-auto flex max-w-[1140px] flex-col items-center gap-10">
                {/* Hero content */}
                <div className="flex w-full flex-col items-center gap-6 text-center">
                  {Array.isArray(kicker) && isFilled.richText(kicker) && (
                    <div className="rounded-full border border-[#00f557] px-5 py-2">
                      <PrismicRichText
                        field={kicker}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-center text-[12px] font-normal uppercase tracking-[0.08em] text-white md:text-[14px]">
                              {children}
                            </p>
                          ),
                          em: ({ children }) => (
                            <em className="italic">{children}</em>
                          ),
                        }}
                      />
                    </div>
                  )}

                  {isFilled.richText(title) && (
                    <PrismicRichText
                      field={title}
                      components={{
                        heading1: ({ children }) => (
                          <h1 className="w-full text-balance text-center text-[36px] font-bold leading-[1.15] text-white md:text-[48px] lg:text-[56px]">
                            {children}
                          </h1>
                        ),
                      }}
                    />
                  )}

                  {isFilled.richText(description) && (
                    <div className="w-full text-center">
                      <PrismicRichText
                        field={description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-pretty text-[16px] leading-[1.5] text-white/85 md:text-[18px]">
                              {children}
                            </p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold">
                              {children}
                            </strong>
                          ),
                          hyperlink: ({ children, node }) => (
                            <a
                              href={node.data.url ?? '#'}
                              className="text-[#00f557] underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {children}
                            </a>
                          ),
                        }}
                      />
                    </div>
                  )}

                  {isFilled.link(cta_link) && (
                    <div className="pt-2">
                      <PrismicNextLink
                        field={cta_link}
                        className="primary-button"
                      >
                        <ArrowRightIcon className="arrow-icon" />
                        {(cta_link as { text?: string }).text}
                      </PrismicNextLink>
                    </div>
                  )}
                </div>

                {/* Industry cards row */}
                {industries.length > 0 && (
                  <div
                    className="grid w-full grid-cols-2 items-stretch gap-4 sm:grid-cols-3 sm:gap-6 lg:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]"
                    style={
                      {
                        '--cols': industries.length,
                      } as CSSProperties
                    }
                  >
                    {industries.map((industry, i) => {
                      const cardCls =
                        'flex w-full min-h-[187px] flex-col items-center justify-center gap-[16px] rounded-[40px] bg-black/40 backdrop-blur-[20.2px] p-[24px] text-center';
                      const inner = (
                        <>
                          {isFilled.image(industry.icon) && (
                            <PrismicNextImage
                              field={industry.icon}
                              className="size-[36px] object-contain"
                              fallbackAlt=""
                            />
                          )}
                          {industry.title && (
                            <p className="text-balance text-[14px] font-bold leading-[1.3] text-white md:text-[16px]">
                              {industry.title}
                            </p>
                          )}
                        </>
                      );
                      const linkField = industry.link;
                      return isFilled.link(linkField) ? (
                        <PrismicNextLink
                          key={`industry-${i}`}
                          field={linkField}
                          className={cardCls}
                        >
                          {inner}
                        </PrismicNextLink>
                      ) : (
                        <div key={`industry-${i}`} className={cardCls}>
                          {inner}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()
        ) : isCenteredSimple ? (
          <div className="relative mx-auto flex max-w-[1080px] flex-col items-center gap-6 text-center">
            {Array.isArray(kicker) && isFilled.richText(kicker) && (
              <div className="rounded-full border border-[#00f557] px-5 py-2">
                <PrismicRichText
                  field={kicker}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-center text-[12px] font-normal uppercase tracking-[0.08em] text-white md:text-[14px]">
                        {children}
                      </p>
                    ),
                    em: ({ children }) => (
                      <em className="italic">{children}</em>
                    ),
                  }}
                />
              </div>
            )}

            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="w-full max-w-[1040px] text-balance break-words text-center text-[28px] font-bold leading-[1.15] text-white sm:text-[36px] md:text-[56px]">
                      {children}
                    </h1>
                  ),
                }}
              />
            )}

            {isFilled.richText(description) && (
              <div className="w-full max-w-[820px] text-center">
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-pretty text-[16px] leading-[1.5] text-white/85 md:text-[18px]">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                    hyperlink: ({ children, node }) => (
                      <a
                        href={node.data.url ?? '#'}
                        className="text-[#00f557] underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    ),
                  }}
                />
              </div>
            )}

            {isFilled.link(cta_link) && (
              <div className="pt-2">
                <PrismicNextLink field={cta_link} className="primary-button">
                  <ArrowRightIcon className="arrow-icon" />
                  {(cta_link as { text?: string }).text}
                </PrismicNextLink>
              </div>
            )}
          </div>
        ) : isSystemIntelligence ? (
          <div className="relative mx-auto flex max-w-[1140px] flex-col items-center gap-10">
            {/* Hero content (no glass card — directly on dark bg) */}
            <div className="relative flex w-full flex-col items-center gap-6">
              {Array.isArray(kicker) && isFilled.richText(kicker) && (
                <div className="rounded-full border border-[#00f557] px-5 py-2">
                  <PrismicRichText
                    field={kicker}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-center text-[12px] font-normal uppercase tracking-[0.04em] text-white md:text-[14px]">
                          {children}
                        </p>
                      ),
                      em: ({ children }) => (
                        <em className="italic">{children}</em>
                      ),
                    }}
                  />
                </div>
              )}

              {isFilled.richText(title) && (
                <PrismicRichText
                  field={title}
                  components={{
                    heading1: ({ children }) => {
                      // Force a clean line break at the first sentence
                      // (period + space) so the headline reads as two
                      // balanced rows on wide viewports instead of an
                      // awkward 3-line wrap.
                      const splitOnFirstPeriod = (
                        nodes: ReactNode,
                      ): ReactNode => {
                        if (typeof nodes === 'string') {
                          const idx = nodes.indexOf('. ');
                          if (idx !== -1 && idx < nodes.length - 2) {
                            return (
                              <>
                                {nodes.slice(0, idx + 1)}
                                <br />
                                {nodes.slice(idx + 2)}
                              </>
                            );
                          }
                        }
                        if (Array.isArray(nodes)) {
                          for (let i = 0; i < nodes.length; i++) {
                            const child = nodes[i];
                            if (typeof child === 'string') {
                              const idx = child.indexOf('. ');
                              if (idx !== -1 && idx < child.length - 2) {
                                return [
                                  ...nodes.slice(0, i),
                                  child.slice(0, idx + 1),
                                  <br key="hero-headline-br" />,
                                  child.slice(idx + 2),
                                  ...nodes.slice(i + 1),
                                ];
                              }
                            }
                          }
                        }
                        return nodes;
                      };
                      return (
                        <h1 className="w-full max-w-[1040px] text-balance break-words text-center text-[30px] font-semibold leading-[1.15] text-white sm:text-[40px] md:text-[64px]">
                          {splitOnFirstPeriod(children)}
                        </h1>
                      );
                    },
                  }}
                />
              )}

              {(() => {
                const highlight = (slice.primary as { highlight?: string })
                  .highlight;
                return highlight ? (
                  <p className="max-w-[1040px] text-center text-[18px] font-semibold leading-[1.4] text-[#00f557] md:text-[24px]">
                    {highlight}
                  </p>
                ) : null;
              })()}

              {isFilled.richText(description) && (
                <div className="w-full max-w-[940px] text-center">
                  <PrismicRichText
                    field={description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-pretty text-[16px] leading-[1.5] text-white md:text-[18px]">
                          {children}
                        </p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold">{children}</strong>
                      ),
                      hyperlink: ({ children, node }) => (
                        <a
                          href={node.data.url ?? '#'}
                          className="text-[#00f557] underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {children}
                        </a>
                      ),
                    }}
                  />
                </div>
              )}

              {systemIntelligenceSubtitle && (
                <p className="text-center text-[14px] font-normal leading-[1.4] text-white md:text-[16px]">
                  {systemIntelligenceSubtitle}
                </p>
              )}

              {systemIntelligenceTagline && (
                <p className="text-center text-[18px] font-semibold leading-[1.4] text-[#00f557] md:text-[24px]">
                  {systemIntelligenceTagline}
                </p>
              )}

              {(isFilled.link(cta_link) ||
                (secondary_cta_link && isFilled.link(secondary_cta_link))) && (
                <div className="flex flex-col items-stretch justify-center gap-4 pt-2 sm:flex-row sm:items-center">
                  {isFilled.link(cta_link) && (
                    <PrismicNextLink
                      field={cta_link}
                      className="primary-button"
                    >
                      <ArrowRightIcon className="arrow-icon" />
                      {(cta_link as { text?: string }).text}
                    </PrismicNextLink>
                  )}
                  {secondary_cta_link && isFilled.link(secondary_cta_link) && (
                    <PrismicNextLink
                      field={secondary_cta_link}
                      className="group inline-flex items-center justify-center whitespace-nowrap rounded-full border border-white bg-transparent from-[#00F2A5] to-[#00C2FF] px-7 py-3.5 text-[16px] font-bold leading-[1.5] text-white transition-all duration-300 hover:border-transparent hover:bg-gradient-to-r hover:shadow-[0_0_15px_rgba(0,242,165,0.8)] hover:text-white"
                    >
                      <ArrowRightIcon className="mr-0 w-0 opacity-0 transition-all duration-300 group-hover:mr-2 group-hover:w-5 group-hover:opacity-100" />
                      {(secondary_cta_link as { text?: string }).text}
                    </PrismicNextLink>
                  )}
                </div>
              )}
            </div>

            {/* Bottom stats — text pairs (prefix + accent) override glass cards
                when `text_stats` is provided. */}
            {textStats.length > 0 ? (
              <div className="relative grid w-full max-w-[1080px] grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-x-8">
                {textStats.slice(0, 3).map((stat, index) => (
                  <div
                    key={`textstat-${index}`}
                    className="flex min-w-0 flex-col items-center justify-center gap-1.5 px-2 py-2 text-center"
                  >
                    {stat.prefix && (
                      <p className="text-[14px] font-normal leading-[1.4] text-white md:text-[15px]">
                        {stat.prefix}
                      </p>
                    )}
                    {stat.accent && (
                      <p
                        className="w-full break-words text-[18px] font-bold leading-[1.3] text-[#a887ff] md:text-[20px]"
                        style={{
                          textShadow: '0px 0px 16px rgba(168,135,255,0.5)',
                        }}
                      >
                        {stat.accent}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              stats.length > 0 && (
                <div className="grid w-full max-w-[1080px] grid-cols-1 gap-6 md:grid-cols-3">
                  {stats.slice(0, 3).map((stat, index) => (
                    <div
                      key={`stat-${index}`}
                      className="flex flex-col items-center rounded-[40px] bg-black/40 p-8 backdrop-blur-[10px] md:p-10"
                    >
                      <StatCounter
                        target={Number(stat.value ?? 0)}
                        suffix={stat.suffix || '%'}
                        className="text-[52px] font-medium leading-[1] text-white md:text-[65px]"
                      />
                      <p
                        className="mt-3 text-center text-[14px] font-medium leading-[1.4] text-[#a887ff] md:text-[16px]"
                        style={{ textShadow: '0px 0px 60px #a887ff' }}
                      >
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        ) : isVelxArchitect ? (
          <div className="relative mx-auto flex max-w-[1140px] flex-col items-center gap-[30px]">
            {/* Glassmorphism content box */}
            <div className="flex w-full flex-col items-center gap-4 rounded-[40px] bg-black/40 p-6 backdrop-blur-[10px] md:p-10">
              {Array.isArray(kicker) && isFilled.richText(kicker) && (
                <div className="rounded-full border border-[#00f557] px-4 py-2">
                  <PrismicRichText
                    field={kicker}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-center text-[12px] font-normal uppercase tracking-[0.04em] text-white md:text-[14px]">
                          {children}
                        </p>
                      ),
                      em: ({ children }) => (
                        <em className="italic">{children}</em>
                      ),
                    }}
                  />
                </div>
              )}

              {isFilled.richText(title) && (
                <PrismicRichText
                  field={title}
                  components={{
                    heading1: ({ children }) => (
                      <h1 className="max-w-[904px] text-center text-[38px] font-semibold capitalize leading-[1.2] text-white md:text-[56px]">
                        {children}
                      </h1>
                    ),
                  }}
                />
              )}

              {isFilled.richText(description) && (
                <div className="w-full max-w-full text-center">
                  <PrismicRichText
                    field={description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-[18px] leading-[1.4] text-white">
                          {children}
                        </p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-normal text-[#00f557]">
                          {children}
                        </strong>
                      ),
                      hyperlink: ({ children, node }) => (
                        <a
                          href={node.data.url ?? '#'}
                          className="text-[#00f557] underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {children}
                        </a>
                      ),
                    }}
                  />
                </div>
              )}

              {(isFilled.link(cta_link) ||
                (secondary_cta_link && isFilled.link(secondary_cta_link))) && (
                <div className="flex flex-col items-center gap-4 pt-2 sm:flex-row sm:justify-center">
                  {isFilled.link(cta_link) && (
                    <PrismicNextLink
                      field={cta_link}
                      className="primary-button inline-flex items-center justify-center rounded-full bg-[#00f557] px-6 py-3 text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90"
                    />
                  )}
                  {secondary_cta_link && isFilled.link(secondary_cta_link) && (
                    <PrismicNextLink
                      field={secondary_cta_link}
                      className="inline-flex items-center justify-center rounded-full border border-white/60 px-6 py-3 text-[16px] font-bold leading-[1.5] text-white transition-colors hover:bg-white/10"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Stats cards */}
            {stats.length > 0 && (
              <div className="grid w-full max-w-[1080px] grid-cols-1 gap-6 md:grid-cols-3">
                {stats.slice(0, 3).map((stat, index) => (
                  <div
                    key={`stat-${index}`}
                    className="flex flex-col items-center rounded-[40px] bg-black/40 p-8 backdrop-blur-[10px] md:p-10"
                  >
                    <StatCounter
                      target={Number(stat.value ?? 0)}
                      suffix={stat.suffix || '%'}
                      className="text-[52px] font-medium leading-[1] text-white md:text-[65px]"
                    />
                    <p
                      className="mt-2 text-[16px] font-medium leading-[1.5] text-[#a887ff] md:whitespace-nowrap"
                      style={{ textShadow: '0px 0px 60px #a887ff' }}
                    >
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Bottom recognition bar */}
            {isVelxArchitect && 'under_announcement_logo' in slice.primary && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                {isFilled.image(slice.primary.under_announcement_logo) && (
                  <PrismicNextImage
                    field={slice.primary.under_announcement_logo}
                    className="h-[22px] w-auto object-contain"
                    fallbackAlt=""
                  />
                )}
                {slice.primary.under_announcement_text && (
                  <p
                    className="text-center text-[18px] font-medium leading-[1.4] text-white"
                    style={{ textShadow: '0px 0px 6px black' }}
                  >
                    {slice.primary.under_announcement_text}
                  </p>
                )}
                {isFilled.link(slice.primary.under_announcement_cta) && (
                  <PrismicNextLink
                    field={slice.primary.under_announcement_cta}
                    className="text-[16px] font-extrabold leading-[1.5] text-[#00f557] underline"
                    style={{ textShadow: '0px 0px 6px black' }}
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="relative mx-auto flex max-w-[1140px] flex-col items-center">
            {Array.isArray(kicker) && isFilled.richText(kicker) && (
              <div className="mb-4 rounded-full border border-[#00f557] px-4 py-2">
                <PrismicRichText
                  field={kicker}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-center text-[12px] font-normal uppercase tracking-[0.04em] text-white md:text-[14px]">
                        {children}
                      </p>
                    ),
                    em: ({ children }) => (
                      <em className="italic">{children}</em>
                    ),
                  }}
                />
              </div>
            )}

            {heroHighlight && (
              <p className="mb-4 max-w-[920px] text-balance text-center text-[14px] font-semibold leading-[1.4] text-[#00f557] md:text-[16px]">
                {heroHighlight}
              </p>
            )}

            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="w-full max-w-[904px] text-balance break-words text-center text-[30px] font-semibold leading-[1.2] text-white sm:text-[38px] md:text-[56px]">
                      {children}
                    </h1>
                  ),
                }}
              />
            )}

            {stats.length > 0 && (
              <div className="mt-6 grid w-full max-w-[760px] grid-cols-1 gap-6 md:mt-8 md:grid-cols-3 md:gap-10">
                {stats.slice(0, 3).map((stat, index) => (
                  <div
                    key={`stat-${index}`}
                    className="flex flex-col items-center justify-center text-center"
                  >
                    <StatCounter
                      target={Number(stat.value ?? 0)}
                      suffix={stat.suffix || '%'}
                      className="bg-gradient-to-b from-[#4b0fea] to-[#a887ff] bg-clip-text text-[52px] font-medium leading-[1] text-transparent"
                    />
                    <p
                      className="mt-2 text-[16px] font-medium leading-[1.5] text-[#a887ff] md:whitespace-nowrap"
                      style={{ textShadow: '0px 0px 60px #a887ff' }}
                    >
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {isFilled.richText(description) && (
              <div className="mt-6 w-[904px] max-w-full text-center md:mt-7">
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) =>
                      (() => {
                        const text = flattenRichTextToString(children);

                        // Split into 2 parts based on the first dash.
                        // Keep the dash in the second part.
                        const emDashIndex = text.indexOf('—');
                        const dashIndex =
                          emDashIndex !== -1 ? emDashIndex : text.indexOf('-');

                        if (dashIndex === -1) {
                          return (
                            <p className="text-balance text-[18px] leading-[1.5] text-white">
                              {children}
                            </p>
                          );
                        }

                        const firstPart = text.slice(0, dashIndex).trimEnd();
                        const secondPart = text.slice(dashIndex);

                        return (
                          <p className="text-balance text-[18px] leading-[1.5] text-white">
                            <span>{firstPart}</span>
                            <span className="text-[#00F557] ml-2">
                              {secondPart}
                            </span>
                          </p>
                        );
                      })(),
                    strong: ({ children }) => (
                      <strong className="font-normal text-[#00f557]">
                        {children}
                      </strong>
                    ),
                    hyperlink: ({ children, node }) => (
                      <a
                        href={node.data.url ?? '#'}
                        className="text-[#00f557] underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    ),
                  }}
                />
              </div>
            )}

            {(isFilled.link(cta_link) ||
              (secondary_cta_link && isFilled.link(secondary_cta_link))) && (
              <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                {secondary_cta_link && isFilled.link(secondary_cta_link) && (
                  <PrismicNextLink
                    field={secondary_cta_link}
                    className="inline-flex items-center justify-center rounded-full border border-white/60 px-6 py-3 text-[16px] font-bold leading-[1.5] text-white transition-colors hover:bg-white/10"
                  />
                )}
                {isFilled.link(cta_link) && (
                  <PrismicNextLink
                    field={cta_link}
                    className="primary-button inline-flex items-center justify-center rounded-full bg-[#00f557] px-6 py-3 text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90"
                  />
                )}
              </div>
            )}

            {hasLogos && (
              <div
                className={`relative mt-20 w-full md:mt-24 ${scrollOverLogos ? 'pb-10 md:pb-12' : ''}`}
              >
                <div
                  className="relative py-10"
                  style={{
                    WebkitMaskImage:
                      'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                    maskImage:
                      'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                  }}
                >
                  <DynamicMarquee speed={30} gradient={false} play={!isPaused}>
                    {doubledLogos.map((item, index) => (
                      <div
                        key={`logo-${index}`}
                        className={
                          showLogoLines
                            ? 'group relative flex w-64 flex-col items-center px-4'
                            : 'group relative flex h-[52px] min-w-[160px] items-center justify-center px-8'
                        }
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                      >
                        {showLogoLines && (
                          <div className="relative h-24 w-full">
                            {(item as unknown as { logo_tooltip?: string })
                              .logo_tooltip && (
                              <div className="pointer-events-none absolute bottom-8 left-1/2 z-20 w-max max-w-xs -translate-x-1/2 rounded-lg border border-white/20 border-t-2 border-t-[#06f273] bg-[#ced7d5] p-2 text-xs text-black opacity-0 shadow-lg backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100">
                                {
                                  (item as unknown as { logo_tooltip?: string })
                                    .logo_tooltip
                                }
                                <div className="absolute left-1/2 top-full h-4 w-px -translate-x-1/2 bg-white/50" />
                              </div>
                            )}
                            <div className="absolute bottom-[5px] left-0 h-px w-[calc(50%-1rem)] bg-white/20" />
                            <div className="absolute bottom-[5px] right-0 h-px w-[calc(50%-1rem)] bg-white/20" />
                            <div className="absolute bottom-[-8px] left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center">
                              <div
                                className={`h-3 w-3 rounded-full border-2 border-white/20 bg-[#01060a] transition-all duration-300 ${
                                  isPaused ? 'scale-100' : 'animate-beat'
                                }`}
                              />
                            </div>
                          </div>
                        )}
                        {isFilled.image(item.logo) &&
                          (() => {
                            const logo = (
                              <div
                                className={
                                  showLogoLines
                                    ? 'mt-4 flex h-[28px] w-44 items-center justify-center opacity-80 transition-opacity duration-500 group-hover:opacity-100'
                                    : 'contents'
                                }
                              >
                                <PrismicNextImage
                                  field={item.logo}
                                  className={
                                    showLogoLines
                                      ? 'h-[28px] w-auto object-contain'
                                      : 'h-[28px] w-auto object-contain opacity-40 transition-opacity duration-200 group-hover:opacity-100'
                                  }
                                  fallbackAlt=""
                                />
                              </div>
                            );
                            // M-10: link the client logo to its case study when set.
                            return isFilled.link(item.logo_link) ? (
                              <PrismicNextLink
                                field={item.logo_link}
                                aria-label={
                                  (
                                    item as unknown as {
                                      logo_tooltip?: string;
                                    }
                                  ).logo_tooltip
                                    ? `View ${(item as unknown as { logo_tooltip?: string }).logo_tooltip} case study`
                                    : 'View client case study'
                                }
                                className="contents"
                              >
                                {logo}
                              </PrismicNextLink>
                            ) : (
                              logo
                            );
                          })()}
                      </div>
                    ))}
                  </DynamicMarquee>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recognition band (e.g. Gartner) pinned to the bottom edge of the
            hero card — breaks out of the section padding to span full width. */}
        {hasRecognition && (
          <div className="relative z-10 -mx-4 -mb-12 mt-16 border-t border-white/10 bg-black/40 px-4 py-4 md:-mx-8 md:px-8 lg:-mx-[150px] lg:-mb-[64px] lg:px-[60px]">
            <div className="mx-auto flex max-w-[1140px] flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center md:justify-start md:text-left">
              {isFilled.image(recognitionLogo) && (
                <PrismicNextImage
                  field={recognitionLogo}
                  className="h-[20px] w-auto object-contain"
                  fallbackAlt=""
                />
              )}
              {isFilled.image(recognitionLogo) &&
                (recognitionText || isFilled.link(recognitionLink)) && (
                  <span
                    aria-hidden
                    className="hidden h-4 w-px bg-white/30 md:inline-block"
                  />
                )}
              {recognitionText && (
                <span className="text-[13px] font-normal leading-[1.4] text-white/80 md:text-[14px]">
                  {recognitionText}
                </span>
              )}
              {isFilled.link(recognitionLink) && (
                <PrismicNextLink
                  field={recognitionLink}
                  className="text-[13px] font-bold leading-[1.4] text-[#00f557] transition-opacity hover:opacity-80 md:text-[14px]"
                />
              )}
            </div>
          </div>
        )}

        {!isVelxArchitect && showScrollHint && !hasLogos && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-5 z-10 flex justify-center md:bottom-8 lg:bottom-10"
            aria-hidden
          >
            <div className="motion-safe:animate-slow-bounce motion-reduce:animate-none">
              <PrismicNextImage
                field={scroll_down_image}
                className="mx-auto block h-[120px] w-auto max-h-[150px] max-w-[40px] object-contain object-bottom opacity-90"
                sizes="40px"
                alt=""
              />
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default HeroVelxEngine;
